import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { FiSearch, FiLogOut, FiUser, FiUsers, FiMenu, FiX, FiArrowLeft } from 'react-icons/fi';
import CsvListPage from './components/CsvListPage';
import PdfListPage from './components/PdfListPage';
import MeteoblueLivePage from './components/MeteoblueLivePage';
import DowntimeReportPage from './components/DowntimeReportPage';
import PlantDetailPage from './components/PlantDetailPage';
import AdminPage from './components/AdminPage';
import LoginPage from './components/LoginPage';
import { authenticatedFetch } from './utils/api';

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transform: scale(${props => (props.$zoomLevel || 1.0) * 0.9});
  transform-origin: top left;
  width: ${props => 100 / ((props.$zoomLevel || 1.0) * 0.9)}%; /* Compensate for the scale */
  height: ${props => 100 / ((props.$zoomLevel || 1.0) * 0.9)}%; /* Compensate for the scale */
  overflow: hidden;
  position: relative;
`;

const HeaderBar = styled.div`
  background: transparent;
  border-bottom: none;
  padding: 16px 32px;
  box-shadow: none;
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-height: 60px;
  gap: 16px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 6px;
  color: white;
  padding: 6px 16px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  grid-column: 1;
  min-width: fit-content;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const MenuButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #333333;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  display: ${props => props.$sidebarOpen ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  backdrop-filter: blur(10px);
  z-index: 1001;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  visibility: ${props => props.$sidebarOpen ? 'hidden' : 'visible'};

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const PortalTitle = styled.h1`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  white-space: nowrap;
  grid-column: 2;
  justify-self: center;
  text-align: center;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const LightningBoltIcon = styled.img`
  height: 1em;
  width: auto;
  display: inline-block;
  vertical-align: baseline;
  align-self: flex-end;
  margin: 0;
  filter: brightness(0) invert(1);
  transform: translateY(0.03em);
`;

const SidebarOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  transition: opacity 0.3s ease;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
`;

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  height: 100vh;
  width: 300px;
  background: white;
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  will-change: transform;
  isolation: isolate;
`;

const SidebarHeader = styled.div`
  padding: 16px 32px;
  background: #0077B6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 60px;
  box-sizing: border-box;
`;

const SidebarTitle = styled.h2`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: -0.5px;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const UserInfoSection = styled.div`
  padding: 20px;
  background: #F4F7F9;
  border-bottom: 1px solid #d0d7de;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const UserNameText = styled.div`
  font-weight: 600;
  font-size: 0.8rem;
  color: #333333;
`;

const UserRoleText = styled.div`
  font-size: 0.7rem;
  color: #666666;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: white;
  color: #333333;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border-bottom: 1px solid #F4F7F9;
  flex-shrink: 0;

  &:hover {
    background: #F4F7F9;
    color: #0077B6;
  }

  &:active {
    background: #e8edf1;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #d0d7de;
  margin: 0;
`;

const MenuItemsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: white;
  min-height: 0;
  overflow-y: auto;
`;

const MenuSpacer = styled.div`
  flex: 1;
  background: white;
  min-height: 0;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 24px;
  background: transparent;
  overflow: hidden;
  min-height: 0;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PlantsScrollContainer = styled.div.attrs({
  'data-scroll-container': 'plants'
})`
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: ${props => props.$maxHeight ? `${props.$maxHeight}px` : '420px'};
  overflow-y: auto;
  padding-right: 0;
  padding-bottom: 0;
  margin-right: 0;
  margin-top: 0;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const PlantsTableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  gap: 0;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(208, 215, 222, 0.8);
`;

const HeaderRowWrapper = styled.div`
  width: 100%;
  padding-right: ${props => props.$hasScrollbar ? '8px' : '0px'};
  box-sizing: border-box;
  transition: padding-right 0.1s ease;
  margin-bottom: 0;
`;

const PlantRow = styled.div`
  display: grid;
  grid-template-columns: 120px 200px 1fr 1fr 120px;
  align-items: center;
  gap: 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: none;
  border-top: 1px solid rgba(208, 215, 222, 0.8);
  padding: 8px 20px;
  border-radius: 0;
  box-shadow: none;
  transition: all 0.2s ease;

  &:first-of-type {
    border-top: none;
  }

  &:hover {
    box-shadow: none;
    background: rgba(255, 255, 255, 1);
  }
`;

const PlantId = styled.div`
  font-weight: 700;
  color: #333333;
  font-size: 0.95rem;
`;

const PlantName = styled.div`
  color: #666666;
  font-size: 0.85rem;
  font-weight: 500;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const Analysis = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const Management = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const BaseActionButton = styled.button`
  color: #333333;
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 10px 18px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.85rem;
  min-width: 120px;
  width: 120px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover:not(:disabled) { 
    background-color: inherit;
  }
  &:active:not(:disabled) { 
    background-color: inherit;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const LiveButton = styled(BaseActionButton)`
  background: white;
  &:hover:not(:disabled) { background: #f5f5f5; }
`;

const PdfButton = styled(BaseActionButton)`
  background: white;
  &:hover:not(:disabled) { background: #f5f5f5; }
`;

const CsvButton = styled(BaseActionButton)`
  background: white;
  &:hover:not(:disabled) { background: #f5f5f5; }
`;

const DowntimeButton = styled(BaseActionButton)`
  background: white;
  &:hover:not(:disabled) { 
    background: #f5f5f5;
  }
`;

const PlantDetailsButton = styled(BaseActionButton)`
  background: white;
  &:hover:not(:disabled) { background: #f5f5f5; }
`;

const PlantCheckbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #0077B6;
`;

const CheckboxColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 100%;
  position: relative;
  width: 100%;
  align-self: stretch;
`;

const GenerateReportHeaderButton = styled.button`
  background: white;
  color: #333333;
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.7rem;
  letter-spacing: 0.3px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 3;
  text-align: center;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  height: 20px;
  line-height: 1;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SelectAllHeaderButton = styled.button`
  background: white;
  color: #333333;
  border: 1px solid rgba(0, 0, 0, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.6rem;
  letter-spacing: 0.5px;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  text-align: center;
  box-sizing: border-box;
  height: 20px;
  line-height: 1;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const HeaderButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 0;
  align-self: stretch;
  margin-left: -24px;
  max-width: 100px;
  height: 100%;
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #333333;
  color: white;
  font-size: 0.7rem;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 1000;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333333;
  }
`;

const GenerateReportButtonContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 3;
  
  &:hover ${Tooltip} {
    opacity: 1;
    pointer-events: auto;
  }
`;

const SearchBarContainer = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(208, 215, 222, 0.8);
  padding: 14px 18px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:focus-within {
    border-color: #0077B6;
    box-shadow: 0 0 0 4px rgba(0, 119, 182, 0.1);
    background: rgba(255, 255, 255, 1);
  }
`;

const SearchIcon = styled(FiSearch)`
  color: #666666;
  margin-right: 12px;
  font-size: 1.2rem;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.9rem;
  color: #333333;
  background: transparent;

  &::placeholder {
    color: #999999;
  }
`;

// Global background component that's not affected by zoom
const GlobalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-image: linear-gradient(to bottom, transparent 0%, transparent 20%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.5) 80%, rgba(255, 255, 255, 0.6) 100%),
                    url('/solar-panel.jpg');
  background-size: 100% 100%, 100% 100%;
  background-position: center center, center center;
  background-repeat: no-repeat, no-repeat;
  z-index: -1;
  pointer-events: none;
`;

// Loading spinner and text for plants loading - matching Loading Live Data style
const PlantsLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background: transparent;
  backdrop-filter: none;
  border-radius: 0;
  box-shadow: none;
  min-height: 400px;
  border: none;
`;

const PlantsLoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 24px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PlantsLoadingText = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  text-align: center;
  letter-spacing: 0.3px;
`;

function App() {
  const [user, setUser] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [selectedView, setSelectedView] = useState(null); // 'live' | 'pdf' | 'csv' | 'downtime' | 'plant-details'
  const [showAdminPage, setShowAdminPage] = useState(false);
  const [showSetAccessPage, setShowSetAccessPage] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [searchQuery, setSearchQuery] = useState('');
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [selectedPlants, setSelectedPlants] = useState(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  const plantsScrollContainerRef = useRef(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [maxVisibleRows, setMaxVisibleRows] = useState(7);
  const [maxContainerHeight, setMaxContainerHeight] = useState(420);

  // Prevent browser's default scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Calculate max container height to fit screen without cutting plants
  useEffect(() => {
    const calculateMaxHeight = () => {
      // Measure actual row height from DOM (in viewport coordinates after transform)
      let rowHeightViewport = 60; // Default fallback in viewport pixels
      const firstRow = document.querySelector('[id^="plant-row-"]');
      if (firstRow) {
        const rect = firstRow.getBoundingClientRect();
        rowHeightViewport = rect.height;
      }
      
      // Convert to CSS pixels (before transform) - divide by zoom to get original size
      const rowHeightCSS = rowHeightViewport / zoomLevel;
      
      // Measure all fixed elements that take up vertical space (in viewport coordinates)
      let fixedHeightViewport = 0;
      
      // Header bar
      const headerBar = document.querySelector('[class*="HeaderBar"]');
      if (headerBar) {
        fixedHeightViewport += headerBar.getBoundingClientRect().height;
      }
      
      // Search bar
      const searchBar = document.querySelector('[class*="SearchBar"]');
      if (searchBar) {
        fixedHeightViewport += searchBar.getBoundingClientRect().height;
      }
      
      // Header row (column names)
      const headerRow = document.querySelector('[class*="HeaderRowWrapper"]');
      if (headerRow) {
        fixedHeightViewport += headerRow.getBoundingClientRect().height;
      }
      
      // Content wrapper padding (measured in viewport, need to convert to CSS)
      const contentWrapper = document.querySelector('[class*="ContentWrapper"]');
      if (contentWrapper) {
        const rect = contentWrapper.getBoundingClientRect();
        const style = window.getComputedStyle(contentWrapper);
        const paddingTop = parseFloat(style.paddingTop) || 0;
        const paddingBottom = parseFloat(style.paddingBottom) || 0;
        // Padding is in CSS pixels, convert to viewport by multiplying by zoom
        fixedHeightViewport += (paddingTop + paddingBottom) * zoomLevel;
      }
      
      // Plants table wrapper padding/border
      const tableWrapper = document.querySelector('[class*="PlantsTableWrapper"]');
      if (tableWrapper) {
        const style = window.getComputedStyle(tableWrapper);
        const paddingTop = parseFloat(style.paddingTop) || 0;
        const paddingBottom = parseFloat(style.paddingBottom) || 0;
        const borderTop = parseFloat(style.borderTopWidth) || 0;
        const borderBottom = parseFloat(style.borderBottomWidth) || 0;
        // Padding/border is in CSS pixels, convert to viewport by multiplying by zoom
        fixedHeightViewport += (paddingTop + paddingBottom + borderTop + borderBottom) * zoomLevel;
      }
      
      // Available viewport height
      const viewportHeight = window.innerHeight;
      
      // Calculate available height for the scroll container (in viewport coordinates)
      const availableHeightViewport = viewportHeight - fixedHeightViewport;
      
      // Add a safety buffer (4px) to prevent rounding errors from cutting rows
      const availableHeightWithBuffer = availableHeightViewport - 4;
      
      // Make it 25% smaller (multiply by 0.75)
      const adjustedHeight = availableHeightWithBuffer * 0.75;
      
      // Calculate how many full rows can fit (in viewport coordinates)
      const maxRows = Math.max(1, Math.floor(adjustedHeight / rowHeightViewport));
      
      // Set max height in CSS pixels (before transform)
      const maxHeightCSS = maxRows * rowHeightCSS;
      
      setMaxVisibleRows(Math.max(1, maxRows));
      setMaxContainerHeight(Math.max(rowHeightCSS, maxHeightCSS));
    };

    // Wait for DOM to be ready
    const timeoutId = setTimeout(calculateMaxHeight, 150);
    calculateMaxHeight();
    window.addEventListener('resize', calculateMaxHeight);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, [plants, searchQuery, zoomLevel]);

  // Check if scrollbar is present in plants scroll container
  useEffect(() => {
    const checkScrollbar = () => {
      if (plantsScrollContainerRef.current) {
        const hasScroll = plantsScrollContainerRef.current.scrollHeight > plantsScrollContainerRef.current.clientHeight;
        setHasScrollbar(hasScroll);
      }
    };

    // Use setTimeout to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      checkScrollbar();
    }, 0);

    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (plantsScrollContainerRef.current) {
      resizeObserver.observe(plantsScrollContainerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [plants, searchQuery, maxVisibleRows]);

  // Check for existing session on mount
  useEffect(() => {
    const storedSessionId = localStorage.getItem('sessionId');
    const storedUser = localStorage.getItem('user');
    
    if (storedSessionId && storedUser) {
      // Verify session is still valid
      authenticatedFetch('/api/auth/me', {
        headers: {
          'x-session-id': storedSessionId
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setUser(JSON.parse(storedUser));
            setSessionId(storedSessionId);
            if (data.permissions) {
              setPermissions(data.permissions);
            }
          } else {
            // Session invalid on backend, but keep it in localStorage
            // User can still try to use the app, will just need to re-login if backend restarted
          }
        })
        .catch(() => {
          // Network error - keep session, user can still try to use the app
        });
    }
    
    // Listen for session updates from auto re-authentication
    const handleSessionUpdate = (event) => {
      setSessionId(event.detail.sessionId);
      setUser(event.detail.user);
    };
    
    window.addEventListener('sessionUpdated', handleSessionUpdate);
    return () => window.removeEventListener('sessionUpdated', handleSessionUpdate);
  }, []);

  // Fetch plants from API
  useEffect(() => {
    if (!user || !sessionId) {
      setLoading(false);
      return;
    }

    const fetchPlants = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await authenticatedFetch('/api/plants', {
          headers: {
            'x-session-id': sessionId
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch plants');
        }
        const data = await response.json();
        // Map API response to frontend format, preserving hasAccess flag
        const formattedPlants = data.plants.map(plant => ({
          id: plant.id,
          name: plant.name || `${plant.id} Plant`,
          hasAccess: plant.hasAccess !== false // Default to true if not specified
        }));
        setPlants(formattedPlants);
      } catch (err) {
        setError('Failed to load plants. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlants();
  }, [user, sessionId]);

  // Fetch user permissions when user changes
  useEffect(() => {
    if (!user || !sessionId) {
      return;
    }

    const fetchPermissions = async () => {
      try {
        const response = await authenticatedFetch('/api/auth/permissions', {
          headers: {
            'x-session-id': sessionId
          }
        });
        const data = await response.json();
        if (data.success && data.permissions) {
          setPermissions(data.permissions);
        }
      } catch (err) {
        // Error fetching permissions - silently fail
      }
    };

    fetchPermissions();
  }, [user, sessionId]);

  const handleLogin = async (userData, session) => {
    setUser(userData);
    setSessionId(session);
    // Fetch user permissions after login
    try {
      const response = await authenticatedFetch('/api/auth/permissions', {
        headers: {
          'x-session-id': session
        }
      });
      const data = await response.json();
      if (data.success && data.permissions) {
        setPermissions(data.permissions);
      }
    } catch (err) {
      // Error fetching permissions - silently fail
    }
  };

  const handleLogout = async () => {
    if (sessionId) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-session-id': sessionId
          },
          body: JSON.stringify({ sessionId })
        });
      } catch (err) {
        // Logout error - continue with local cleanup
      }
    }
    localStorage.removeItem('sessionId');
    localStorage.removeItem('user');
    setUser(null);
    setSessionId(null);
    setSelectedPlant(null);
    setSelectedView(null);
    setPlants([]);
    setSelectedPlants(new Set());
  };

  // Handle zoom with + and - keys
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '+' || e.key === '=') {
        setZoomLevel(prev => Math.min(prev * 1.1, 2.0));
      } else if (e.key === '-') {
        setZoomLevel(prev => Math.max(prev / 1.1, 0.3));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close sidebar on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [menuOpen]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // Helper function to check if user has permission for a feature
  const hasPermission = (featureKey) => {
    // Admin always has all permissions
    if (user?.role === 'admin') {
      return true;
    }
    // Check user-specific permissions
    // If permission is explicitly set to false, deny access
    if (permissions && permissions[featureKey] === false) {
      return false;
    }
    // Default: allow access if not explicitly denied
    return true;
  };

  const openView = (plant, view) => {
    // Check feature permission
    if (!hasPermission(view)) {
      alert('You do not have permission to access this feature.');
      return;
    }
    // Check plant access
    if (!plant.hasAccess) {
      alert('You do not have access to this plant.');
      return;
    }
    // Save which plant we're viewing so we can center it when returning
    sessionStorage.setItem('lastViewedPlantId', plant.id);
    setSelectedPlant(plant);
    setSelectedView(view);
  };

  const handleBackToPlants = () => {
    setSelectedPlant(null);
    setSelectedView(null);
    setShowAdminPage(false);
    scrollRestoredRef.current = false; // Reset flag to allow restoration
  };

  // Track scroll position while on main page
  useEffect(() => {
    if (!selectedPlant && !selectedView && !showAdminPage) {
      const handleScroll = () => {
        const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        sessionStorage.setItem('mainPageScrollPosition', scrollPosition.toString());
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [selectedPlant, selectedView, showAdminPage]);

  // Helper function to scroll to plant center
  const scrollToPlantCenter = useCallback((plantId) => {
    const plantElement = document.getElementById(`plant-row-${plantId}`);
    if (!plantElement) {
      return false;
    }

    // Find the PlantsScrollContainer using data attribute
    const scrollContainer = plantElement.closest('[data-scroll-container="plants"]');
    
    if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
      // Scroll the container to center the plant
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = plantElement.getBoundingClientRect();
      const relativeTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
      const middle = relativeTop - (containerRect.height / 2) + (elementRect.height / 2);
      
      scrollContainer.scrollTop = Math.max(0, middle);
      sessionStorage.removeItem('lastViewedPlantId');
      return true;
    } else {
      // Fallback to window scroll
      const elementRect = plantElement.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = Math.max(0, absoluteElementTop - (window.innerHeight / 2) + (elementRect.height / 2));
      
      window.scrollTo({ top: middle, behavior: 'auto' });
      document.documentElement.scrollTop = middle;
      document.body.scrollTop = middle;
      sessionStorage.removeItem('lastViewedPlantId');
      return true;
    }
  }, []);

  // Scroll to center the last viewed plant when returning to main page
  useEffect(() => {
    if (!selectedPlant && !selectedView && !showAdminPage && !loading && plants.length > 0) {
      const lastViewedPlantId = sessionStorage.getItem('lastViewedPlantId');
      
      if (lastViewedPlantId && !scrollRestoredRef.current) {
        // Check if plant exists in the plants list
        const plantExists = plants.some(p => p.id === lastViewedPlantId);
        
        if (plantExists) {
          scrollRestoredRef.current = true; // Mark as restored immediately
          
          // Clear search to ensure plant is visible
          if (searchQuery.trim() !== '') {
            setSearchQuery('');
          }
          
          // Wait for DOM to update, then scroll - try multiple times
          let attempts = 0;
          const maxAttempts = 20;
          
          const tryScroll = () => {
            attempts++;
            if (scrollToPlantCenter(lastViewedPlantId)) {
              return; // Success
            }
            if (attempts < maxAttempts) {
              setTimeout(tryScroll, 100);
            }
          };
          
          // Start trying with various delays
          setTimeout(tryScroll, 50);
          setTimeout(tryScroll, 150);
          setTimeout(tryScroll, 300);
          requestAnimationFrame(() => {
            setTimeout(tryScroll, 50);
          });
        } else {
          // Plant doesn't exist, clear the stored ID
          scrollRestoredRef.current = true;
          sessionStorage.removeItem('lastViewedPlantId');
        }
      }
    }
  }, [selectedPlant, selectedView, showAdminPage, loading, plants, searchQuery, scrollToPlantCenter]);

  const handleShowAdminPage = () => {
    setShowAdminPage(true);
  };

  const handleTogglePlant = (plantId) => {
    // Check if user has access to this plant
    const plant = plants.find(p => p.id === plantId);
    if (plant && !plant.hasAccess) {
      alert('You do not have access to this plant.');
      return;
    }
    setSelectedPlants(prev => {
      const newSet = new Set(prev);
      if (newSet.has(plantId)) {
        newSet.delete(plantId);
      } else {
        newSet.add(plantId);
      }
      return newSet;
    });
  };

  const handleGenerateCombinedReport = async () => {
    if (selectedPlants.size < 2) {
      return;
    }

    // Check if user has access to all selected plants
    const plantIds = Array.from(selectedPlants);
    const inaccessiblePlants = plantIds.filter(plantId => {
      const plant = plants.find(p => p.id === plantId);
      return plant && !plant.hasAccess;
    });
    
    if (inaccessiblePlants.length > 0) {
      alert(`You do not have access to the following plants: ${inaccessiblePlants.join(', ')}`);
      return;
    }

    try {
      const response = await authenticatedFetch('/api/reports/combined', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plantIds })
      });

      if (!response.ok) {
        let errorMessage = 'Failed to generate report';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `combined_report_${dateStr}.xlsx`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      alert(`Error generating report: ${error.message}`);
    }
  };

  // Show login page if not authenticated
  if (!user || !sessionId) {
    return (
      <>
        <GlobalBackground />
        <LoginPage onLogin={handleLogin} />
      </>
    );
  }

  // Show admin page if admin and showAdminPage is true
  if (showAdminPage && user.role === 'admin') {
    return (
      <>
        <GlobalBackground />
        <AppContainer $zoomLevel={zoomLevel}>
        {!showSetAccessPage && (
          <HeaderBar>
            <BackButton onClick={handleBackToPlants}>
              <FiArrowLeft size={16} />
              HOME
            </BackButton>
            <PortalTitle>
              TimeWatt
              <LightningBoltIcon src="/lightning-bolt.png" alt="Lightning Bolt" />
            </PortalTitle>
            <div></div>
          </HeaderBar>
        )}
        <AdminPage onBack={handleBackToPlants} onSetAccessPageChange={setShowSetAccessPage} />
      </AppContainer>
      </>
    );
  }

  // Filter plants by ID or name based on search query, and only show plants user has access to
  const filteredPlants = plants.filter(plant => {
    // Only show plants the user has access to
    if (!plant.hasAccess) return false;
    // Then filter by search query if provided
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    return plant.id.toLowerCase().includes(query) || plant.name.toLowerCase().includes(query);
  }).sort((a, b) => {
    // Extract numeric part from plant ID (remove "P" prefix)
    const getNumericId = (id) => {
      const match = id.match(/\d+/);
      return match ? parseInt(match[0], 10) : 0;
    };
    return getNumericId(a.id) - getNumericId(b.id);
  });

  const handleSelectAll = () => {
    // Select all filtered plants (only those the user has access to)
    const allFilteredPlantIds = filteredPlants.map(plant => plant.id);
    const allSelected = allFilteredPlantIds.length > 0 && allFilteredPlantIds.every(id => selectedPlants.has(id));
    
    if (allSelected) {
      // Deselect all
      setSelectedPlants(new Set());
    } else {
      // Select all
      setSelectedPlants(new Set(allFilteredPlantIds));
    }
  };

  if (selectedPlant && selectedView === 'live') {
    if (!hasPermission('forecast')) {
      handleBackToPlants();
      return null;
    }
    // Get all accessible plants for navigation (not filtered by search query)
    const accessiblePlants = plants.filter(plant => plant.hasAccess);
    const handleNavigateToPlant = (plant) => {
      openView(plant, 'live');
    };
    return (
      <>
        <GlobalBackground />
        <AppContainer $zoomLevel={zoomLevel}>
          <HeaderBar>
            <BackButton onClick={handleBackToPlants}>
              <FiArrowLeft size={16} />
              HOME
            </BackButton>
            <PortalTitle>
              TimeWatt
              <LightningBoltIcon src="/lightning-bolt.png" alt="Lightning Bolt" />
            </PortalTitle>
            <div></div>
          </HeaderBar>
          <MeteoblueLivePage 
            plant={selectedPlant} 
            onBack={handleBackToPlants}
            plants={accessiblePlants}
            onNavigateToPlant={handleNavigateToPlant}
            permissions={permissions}
          />
        </AppContainer>
      </>
    );
  }
  if (selectedPlant && selectedView === 'pdf') {
    if (!hasPermission('pdf')) {
      handleBackToPlants();
      return null;
    }
    return (
      <>
        <GlobalBackground />
        <AppContainer $zoomLevel={zoomLevel}>
          <HeaderBar>
            <BackButton onClick={handleBackToPlants}>
              <FiArrowLeft size={16} />
              HOME
            </BackButton>
            <PortalTitle>
              TimeWatt
              <LightningBoltIcon src="/lightning-bolt.png" alt="Lightning Bolt" />
            </PortalTitle>
            <div></div>
          </HeaderBar>
          <PdfListPage plant={selectedPlant} onBack={handleBackToPlants} />
        </AppContainer>
      </>
    );
  }
  if (selectedPlant && selectedView === 'csv') {
    if (!hasPermission('csv')) {
      handleBackToPlants();
      return null;
    }
    return (
      <>
        <GlobalBackground />
        <AppContainer $zoomLevel={zoomLevel}>
          <HeaderBar>
            <BackButton onClick={handleBackToPlants}>
              <FiArrowLeft size={16} />
              HOME
            </BackButton>
            <PortalTitle>
              TimeWatt
              <LightningBoltIcon src="/lightning-bolt.png" alt="Lightning Bolt" />
            </PortalTitle>
            <div></div>
          </HeaderBar>
          <CsvListPage plant={selectedPlant} onBack={handleBackToPlants} />
        </AppContainer>
      </>
    );
  }
  if (selectedPlant && selectedView === 'downtime') {
    if (!hasPermission('downtime')) {
      handleBackToPlants();
      return null;
    }
    return (
      <>
        <GlobalBackground />
        <AppContainer $zoomLevel={zoomLevel}>
          <HeaderBar>
            <BackButton onClick={handleBackToPlants}>
              <FiArrowLeft size={16} />
              HOME
            </BackButton>
            <PortalTitle>
              TimeWatt
              <LightningBoltIcon src="/lightning-bolt.png" alt="Lightning Bolt" />
            </PortalTitle>
            <div></div>
          </HeaderBar>
          <DowntimeReportPage plant={selectedPlant} onBack={handleBackToPlants} />
        </AppContainer>
      </>
    );
  }
  if (selectedPlant && selectedView === 'plant-details') {
    if (!hasPermission('plant-details')) {
      handleBackToPlants();
      return null;
    }
    return (
      <>
        <GlobalBackground />
        <AppContainer $zoomLevel={zoomLevel}>
          <HeaderBar>
            <BackButton onClick={handleBackToPlants}>
              <FiArrowLeft size={16} />
              HOME
            </BackButton>
            <PortalTitle>
              TimeWatt
              <LightningBoltIcon src="/lightning-bolt.png" alt="Lightning Bolt" />
            </PortalTitle>
            <div></div>
          </HeaderBar>
          <PlantDetailPage plant={selectedPlant} onBack={handleBackToPlants} />
        </AppContainer>
      </>
    );
  }

  return (
    <>
      <GlobalBackground />
      <SidebarOverlay $isOpen={menuOpen} onClick={() => setMenuOpen(false)} />
      <Sidebar $isOpen={menuOpen} ref={menuRef}>
        <SidebarHeader>
          <SidebarTitle>
            Menu
          </SidebarTitle>
          <CloseButton onClick={() => setMenuOpen(false)}>
            <FiX />
          </CloseButton>
        </SidebarHeader>
        <UserInfoSection>
          <FiUser size={24} style={{ color: '#0077B6' }} />
          <UserName>
            <UserNameText>{user.username}</UserNameText>
            {user.role.toLowerCase() !== user.username.toLowerCase() && (
              <UserRoleText>{user.role}</UserRoleText>
            )}
          </UserName>
        </UserInfoSection>
        <MenuItemsContainer>
          {user.role === 'admin' && (
            <>
              <MenuItem onClick={() => {
                handleShowAdminPage();
                setMenuOpen(false);
              }}>
                <FiUsers size={20} />
                User Management
              </MenuItem>
              <MenuDivider />
            </>
          )}
          <MenuItem onClick={() => {
            handleLogout();
            setMenuOpen(false);
          }}>
            <FiLogOut size={20} />
            Logout
          </MenuItem>
          <MenuSpacer />
        </MenuItemsContainer>
      </Sidebar>
      <AppContainer $zoomLevel={zoomLevel}>
      <HeaderBar>
        <MenuButton $sidebarOpen={menuOpen} onClick={() => setMenuOpen(!menuOpen)}>
          <FiMenu />
        </MenuButton>
        <PortalTitle>
          TimeWatt
          <LightningBoltIcon src="/lightning-bolt.png" alt="Lightning Bolt" />
        </PortalTitle>
        <div></div>
      </HeaderBar>
      <MainContent>
        <ContentWrapper>
          <SearchBarContainer>
            <SearchBar>
              <SearchIcon />
              <SearchInput
                type="text"
                placeholder="Search by Plant ID or Name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchBar>
          </SearchBarContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {loading ? (
              <PlantsLoadingContainer>
                <PlantsLoadingSpinner />
                <PlantsLoadingText>Loading plants...</PlantsLoadingText>
              </PlantsLoadingContainer>
            ) : error ? (
              <div style={{ textAlign: 'center', color: '#dc2626', padding: '32px' }}>
                {error}
              </div>
            ) : filteredPlants.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '32px' }}>
                No plants found matching "{searchQuery}"
              </div>
            ) : (
              <PlantsTableWrapper>
                <HeaderRowWrapper $hasScrollbar={hasScrollbar}>
                  <PlantRow style={{ background: '#e8edf1', fontWeight: 700, padding: '8px 20px', border: 'none', borderBottom: '1px solid #d0d7de', boxShadow: 'none' }}>
                  <PlantId style={{ color: '#333333', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Plant ID</PlantId>
                  <PlantName style={{ color: '#333333', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Name</PlantName>
                  <div style={{ color: '#333333', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Analysis</div>
                  <div style={{ color: '#333333', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Management</div>
                    <CheckboxColumn>
                  <HeaderButtonsContainer>
                    <GenerateReportButtonContainer>
                      {selectedPlants.size < 2 && (
                        <Tooltip>Select 2 plants or more</Tooltip>
                      )}
                      <GenerateReportHeaderButton 
                        disabled={selectedPlants.size < 2}
                        onClick={handleGenerateCombinedReport}
                      >
                        Generate Report
                      </GenerateReportHeaderButton>
                    </GenerateReportButtonContainer>
                    <SelectAllHeaderButton 
                      onClick={handleSelectAll}
                      disabled={filteredPlants.length === 0}
                    >
                      ALL
                    </SelectAllHeaderButton>
                  </HeaderButtonsContainer>
                    </CheckboxColumn>
                </PlantRow>
                </HeaderRowWrapper>
                <PlantsScrollContainer ref={plantsScrollContainerRef} $maxHeight={maxContainerHeight}>
                  {filteredPlants.map((plant) => (
                    <PlantRow key={plant.id} id={`plant-row-${plant.id}`}>
                      <PlantId>{plant.id}</PlantId>
                      <PlantName>{plant.name}</PlantName>
                      <Analysis>
                        <LiveButton 
                          onClick={() => openView(plant, 'live')}
                          disabled={!hasPermission('forecast') || !plant.hasAccess}
                        >
                          FORECAST
                        </LiveButton>
                        <PdfButton 
                          onClick={() => openView(plant, 'pdf')}
                          disabled={!hasPermission('pdf') || !plant.hasAccess}
                        >
                          PDF Reports
                        </PdfButton>
                        <CsvButton 
                          onClick={() => openView(plant, 'csv')}
                          disabled={!hasPermission('csv') || !plant.hasAccess}
                        >
                          CSV Reports
                        </CsvButton>
                      </Analysis>
                      <Management>
                        <DowntimeButton 
                          onClick={() => openView(plant, 'downtime')}
                          disabled={!hasPermission('downtime') || !plant.hasAccess}
                        >
                          Downtime
                        </DowntimeButton>
                        <PlantDetailsButton 
                          onClick={() => openView(plant, 'plant-details')}
                          disabled={!hasPermission('plant-details') || !plant.hasAccess}
                        >
                          Plant Details
                        </PlantDetailsButton>
                      </Management>
                      <CheckboxColumn>
                        <div style={{ marginLeft: '-24px', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', maxWidth: '100px', height: '100%' }}>
                        <PlantCheckbox
                          type="checkbox"
                          checked={selectedPlants.has(plant.id)}
                          onChange={() => handleTogglePlant(plant.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={!plant.hasAccess}
                        />
                        </div>
                      </CheckboxColumn>
                    </PlantRow>
                  ))}
                </PlantsScrollContainer>
              </PlantsTableWrapper>
            )}
          </div>
        </ContentWrapper>
      </MainContent>
    </AppContainer>
    </>
  );
}

export default App;
